import { motion } from "framer-motion";
import { Landmark, Users, Plus } from "lucide-react";
import { useContent } from "@/content/ContentContext";

const EASE = [0.16, 1, 0.3, 1];
const SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-12", "md:col-span-12"];

export default function NarrativeManifesto() {
  const { content } = useContent();
  const m = content.manifesto;

  return (
    <section id="narrative" className="relative py-24 sm:py-32" data-testid="narrative-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="max-w-3xl"
        >
          <p className="font-mono-lux text-xs uppercase tracking-[0.3em] text-gold mb-5" data-testid="narrative-eyebrow">
            {m.eyebrow}
          </p>
          <h2 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#F7F7F8]" data-testid="narrative-heading">
            {m.headingPre}<span className="italic text-gold-champagne">{m.headingAccent}</span>
            {m.headingPost}
          </h2>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {m.chapters.map((c, i) => (
            <motion.article
              key={c.num || i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.85, ease: EASE, delay: i * 0.14 }}
              className={`${SPANS[i % SPANS.length]} group relative bg-surface border border-[rgba(212,175,55,0.16)] p-8 sm:p-10 hover:border-[rgba(212,175,55,0.45)] hover:bg-elevated transition-colors duration-500`}
              data-testid={`manifesto-chapter-${c.num}`}
            >
              <span aria-hidden="true" className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[rgba(212,175,55,0.6)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="flex items-start justify-between gap-6">
                <div>
                  <span className="font-mono-lux text-sm text-gold tracking-[0.2em]">CH.{c.num}</span>
                  <h3 className="mt-3 font-serif-lux text-2xl sm:text-3xl text-[#F7F7F8] tracking-tight">{c.title}</h3>
                </div>
                <span aria-hidden="true" className="font-serif-lux text-6xl sm:text-7xl leading-none text-[rgba(212,175,55,0.14)] group-hover:text-[rgba(212,175,55,0.3)] transition-colors duration-500 select-none">
                  {c.num}
                </span>
              </div>
              {c.hats && (
                <div className="mt-8 relative grid grid-cols-2 border border-[rgba(212,175,55,0.22)] overflow-hidden" data-testid="two-hats-visual">
                  <motion.div
                    initial={{ opacity: 0, x: -32 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
                    className="px-4 sm:px-8 py-8 sm:py-10 text-center bg-[#0A0A0C]"
                  >
                    <Landmark size={26} strokeWidth={1.25} className="mx-auto text-gold" aria-hidden="true" />
                    <p className="mt-4 font-mono-lux text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gold">The Boardroom</p>
                    <p className="mt-1.5 font-serif-lux italic text-sm sm:text-base text-[#B8B8C2]">Strategy · Systems · Structure</p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: 32 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
                    className="px-4 sm:px-8 py-8 sm:py-10 text-center bg-[#0D0C09] border-l border-[rgba(212,175,55,0.22)]"
                  >
                    <Users size={26} strokeWidth={1.25} className="mx-auto text-gold" aria-hidden="true" />
                    <p className="mt-4 font-mono-lux text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gold">The Field</p>
                    <p className="mt-1.5 font-serif-lux italic text-sm sm:text-base text-[#B8B8C2]">People · Duplication · Momentum</p>
                  </motion.div>
                  <motion.span
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, ease: EASE, delay: 0.55 }}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-gold text-[#0A0A0B] flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.45)]"
                    aria-hidden="true"
                  >
                    <Plus size={18} strokeWidth={2.5} />
                  </motion.span>
                </div>
              )}
              <p className="mt-6 font-serif-lux italic text-lg sm:text-xl text-gold-champagne leading-snug">
                “{c.quote}”
              </p>
              <p className="mt-5 text-base text-[#A0A0AD] leading-relaxed max-w-3xl">{c.body}</p>
              {c.hats && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {c.hats.map((h, j) => (
                    <div
                      key={j}
                      className="border border-[rgba(212,175,55,0.18)] bg-[#0A0A0C] p-6 sm:p-7 hover:border-[rgba(212,175,55,0.45)] transition-colors duration-500"
                      data-testid={`rare-combination-${(h.label || `hat-${j}`).toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <p className="font-mono-lux text-[10px] uppercase tracking-[0.25em] text-gold">{h.label}</p>
                      <h4 className="mt-2.5 font-serif-lux text-xl sm:text-2xl text-[#F7F7F8] tracking-tight">{h.name}</h4>
                      <p className="mt-3 text-sm text-[#A0A0AD] leading-relaxed">{h.detail}</p>
                    </div>
                  ))}
                </div>
              )}
              {c.closing && (
                <p className="mt-8 font-serif-lux italic text-xl sm:text-2xl text-gold-champagne" data-testid="rare-combination-closing">
                  {c.closing}
                </p>
              )}
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
